import React from 'react';
import { Box, ListPrice, Pricing, ShortTaxMessage, SkeletonText, SkeletonTextLine } from '@vp/swan';
import { usePricingContextIfProvided } from '../../hooks/usePricingContextIfProvided';
import { useVatMessaging } from '../../hooks/useVatMessaging';
import { priceFromCents } from '../../utils/priceFromCents';
import type { PriceType } from '../../types';

export type PriceProps = {
    price?: PriceType;
    loading?: boolean;
    subtleTaxMessage?: boolean;
    stacked?: boolean;
};

export function Price({
    price,
    loading = false,
    subtleTaxMessage = false,
    stacked = false,
}: PriceProps): React.ReactNode {
    if (!price && !loading) {
        return null;
    }

    return (
        <Box as="span" data-testid="price-container" aria-live="polite" aria-busy={loading}>
            <PriceContent
                price={price}
                loading={loading}
                subtleTaxMessage={subtleTaxMessage}
                stacked={stacked}
            />
        </Box>
    );
}

type PriceContentProps = {
    price: PriceType | undefined;
    loading: boolean;
    subtleTaxMessage: boolean;
    stacked: boolean;
};

function PriceContent({ price, loading, subtleTaxMessage, stacked }: PriceContentProps) {
    const pricingContext = usePricingContextIfProvided();
    const vatMessage = useVatMessaging();

    if (loading) {
        return (
            <SkeletonText data-testid="price-skeleton">
                <SkeletonTextLine width="narrow" />
            </SkeletonText>
        );
    }

    if (!price) {
        return null;
    }

    const amount = pricingContext?.vatInclusive ? price.listPrice.taxed : price.listPrice.untaxed;
    const formattedPrice = priceFromCents({ amount, currency: price.currency });

    const stackedStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
    };

    return (
        <Pricing style={stacked ? stackedStyle : undefined}>
            <ListPrice>{formattedPrice}</ListPrice>
            {vatMessage && (
                <ShortTaxMessage
                    fontSkin={subtleTaxMessage ? 'body-standard' : undefined}
                    textColor={subtleTaxMessage ? 'subtle' : undefined}
                    style={{ whiteSpace: 'nowrap' }}
                >
                    {` (${vatMessage})`}
                </ShortTaxMessage>
            )}
        </Pricing>
    );
}
